import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getBulkJobStatus } from '@/api/domains'
import { BulkJobStatusResponse } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ArrowLeft, CheckCircle2, XCircle, Loader2, ExternalLink } from 'lucide-react'
import { Progress } from '@/components/ui/progress'

const POLL_INTERVAL_MS = 2000

interface BulkJobViewProps {
  jobId: string
  backHref?: string
  onBack?: () => void
}

export function BulkJobView({ jobId, backHref, onBack }: BulkJobViewProps) {
  const [jobStatus, setJobStatus] = useState<BulkJobStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => () => stopPolling(), [stopPolling])

  const poll = useCallback(async (id: string) => {
    const result = await getBulkJobStatus(id)
    if (!result.success || !result.data) {
      pollingRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS)
      return
    }
    setJobStatus(result.data)
    setLoading(false)
    if (result.data.status !== 'COMPLETED' && result.data.status !== 'FAILED') {
      pollingRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS)
    }
  }, [])

  useEffect(() => {
    poll(jobId)
  }, [jobId, poll])

  const backButton = backHref ? (
    <Link to={backHref}>
      <Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
    </Link>
  ) : onBack ? (
    <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
  ) : null

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!jobStatus) return null

  const isActive = jobStatus.status === 'PENDING' || jobStatus.status === 'PROCESSING'

  // ── Polling / progress view ──────────────────────────────────────────────
  if (isActive) {
    const progressPct = jobStatus.total > 0
      ? Math.round((jobStatus.processed / jobStatus.total) * 100)
      : 0

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          {backButton}
          <div>
            <h1 className="text-2xl font-bold">Processing Bulk Job</h1>
            <p className="text-muted-foreground font-mono text-sm">{jobId}</p>
          </div>
        </div>

        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Verifying domains
            </CardTitle>
            <CardDescription>{jobStatus.processed} of {jobStatus.total} processed</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={progressPct} className="h-2" />
            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div className="text-xl font-bold">{jobStatus.total}</div>
                <div className="text-muted-foreground">Total</div>
              </div>
              <div>
                <div className="text-xl font-bold text-teal-600">{jobStatus.success_count}</div>
                <div className="text-muted-foreground">Succeeded</div>
              </div>
              <div>
                <div className="text-xl font-bold text-rose-600">{jobStatus.failure_count}</div>
                <div className="text-muted-foreground">Failed</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Results view ─────────────────────────────────────────────────────────
  const successRate = jobStatus.total > 0
    ? Math.round((jobStatus.success_count / jobStatus.total) * 100)
    : 0
  const successResults = jobStatus.results?.filter((r) => r.success) ?? []
  const failedResults = jobStatus.results?.filter((r) => !r.success) ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        {backButton}
        <div>
          <h1 className="text-2xl font-bold">Bulk Verification Complete</h1>
          <p className="text-muted-foreground">Processed {jobStatus.total} domains</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Statistics</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <div className="text-3xl font-bold">{jobStatus.total}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-teal-500/10">
              <div className="text-3xl font-bold text-teal-600">{jobStatus.success_count}</div>
              <div className="text-sm text-teal-600">Successful</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-rose-500/10">
              <div className="text-3xl font-bold text-rose-600">{jobStatus.failure_count}</div>
              <div className="text-sm text-rose-600">Failed</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>Success Rate</span>
              <span className="font-medium">{successRate}%</span>
            </div>
            <Progress value={successRate} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {successResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-teal-500" />
              Successfully Added ({successResults.length})
            </CardTitle>
            <CardDescription>Click a domain to view its details and DNS records</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border max-h-[300px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {successResults.map((item) => (
                    <TableRow key={item.domain}>
                      <TableCell className="font-mono text-sm font-medium">{item.domain}</TableCell>
                      <TableCell>
                        <Link to={`/dashboard/domains/${encodeURIComponent(item.domain)}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {failedResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-rose-500" />
              Failed ({failedResults.length})
            </CardTitle>
            <CardDescription>These domains could not be verified</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border max-h-[300px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failedResults.map((item) => (
                    <TableRow key={item.domain}>
                      <TableCell className="font-mono text-sm">{item.domain}</TableCell>
                      <TableCell className="text-sm text-rose-600">{item.error || 'Unknown error'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-4">
        <Link to="/dashboard/domains">
          <Button>View All Domains</Button>
        </Link>
        {backHref && (
          <Link to={backHref}>
            <Button variant="outline">Back to Verify</Button>
          </Link>
        )}
        {onBack && (
          <Button variant="outline" onClick={onBack}>Back to Verify</Button>
        )}
      </div>
    </div>
  )
}
